import React from "react";
import Tether from "tether";
import { useI18n } from "../domain/i18n";
import { OrganizationR } from "../domain/organization";

export function PageHeader({ title, intro, quote }: { title: string; intro?: string; quote?: string }) {
  return (
    <header className="page-header">
      <div className="page-header__primary">
        <div className="page-header__content">
          <h1 className="page-header__title">{title}</h1>
          {intro && <div className="page-header__lead" dangerouslySetInnerHTML={{ __html: intro }} />}
        </div>
      </div>
      {quote && (
        <div className="page-header__secondary">
          <div className="page-header__content">
            <div className="page-header__quote" dangerouslySetInnerHTML={{ __html: quote }} />
          </div>
        </div>
      )}
    </header>
  );
}

export interface PageHeaderJSProps {
  title: string;
  toggle: boolean;
  lead?: string;
  previous_url?: string;
  previous_label?: string;
  next_url?: string;
  next_label?: string;
  toolbar_left?: React.ReactNode;
  toolbar_right?: React.ReactNode;
  tabbed_content_left?: React.ReactNode;
  tabbed_content_right?: React.ReactNode;
  organizations?: Array<OrganizationR>;
}

export function PageHeaderJS({
  title,
  lead,
  toggle = false,
  previous_url,
  previous_label,
  next_url,
  next_label,
  toolbar_left,
  toolbar_right,
  tabbed_content_left,
  tabbed_content_right,
  organizations,
}: PageHeaderJSProps) {
  const I18n = useI18n();
  const { toggleRef, menuRef } = useOrganizationMenuToggle();

  return (
    <>
      <header className="page-header">
        <div className="page-header__primary">
          <div className="page-header__content">
            {toggle ? (
              <h1 ref={toggleRef} className="page-header__title page-header__title--toggle">
                {title}
              </h1>
            ) : (
              <h1 className="page-header__title">{title}</h1>
            )}
            {lead && <div className="page-header__lead" dangerouslySetInnerHTML={{ __html: lead }} />}

            {previous_url && (
              <a className="page-header__previous" href={previous_url} title={previous_label || "Vorheriges"}>
                <i className="icon icon-arrow-left"></i>
              </a>
            )}
            {next_url && (
              <a className="page-header__next" href={next_url} title={next_label || "Nächstes"}>
                <i className="icon icon-arrow-right"></i>
              </a>
            )}

            <div className={`page-header__toolbar ${lead ? "page-header__toolbar--compact" : ""}`}>
              <div className="page-header__toolbar__left">{toolbar_left}</div>
              <div className="page-header__toolbar__right">{toolbar_right}</div>
            </div>
          </div>
        </div>
        <div className="page-header__secondary">
          <div className="page-header__content clearfix">
            <div className="float-left">{tabbed_content_left}</div>
            <div className="float-right">{tabbed_content_right}</div>
          </div>
        </div>
      </header>
      {organizations && (
        <div ref={menuRef} style={{ display: "none", zIndex: 1000 }}>
          <div className="organization-menu">
            <div className="organization-menu-inner">
              {organizations.map((x) => (
                <a key={x.id} href={`/${I18n.locale}/atlas/${x.id}`} className="organization-menu-link" title={x.name}>
                  {x.name}
                </a>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// -----------------------------------------------------------------------------

function useOrganizationMenuToggle() {
  const toggleRef = React.useRef<HTMLDivElement>(null);
  const menuRef = React.useRef<HTMLDivElement>(null);
  const isVisible = React.useRef(false);

  const showMenu = React.useCallback((evt: MouseEvent) => {
    if (!isVisible.current) {
      evt.stopPropagation();
      if (menuRef.current) menuRef.current.style.display = "";
      isVisible.current = true;
    }
  }, []);

  const hideMenu = React.useCallback(() => {
    if (menuRef.current) menuRef.current.style.display = "none";
    isVisible.current = false;
  }, []);

  React.useEffect(() => {
    let tether: Tether | undefined;
    if (toggleRef.current && menuRef.current) {
      tether = new Tether({
        target: toggleRef.current,
        element: menuRef.current,
        attachment: "top left",
        targetAttachment: "bottom left",
        offset: "5px 0",
      });

      document.addEventListener("click", hideMenu, false);
      toggleRef.current.addEventListener("click", showMenu);
    }

    const toggle = toggleRef.current;
    return () => {
      document.removeEventListener("click", hideMenu);
      toggle && toggle.removeEventListener("click", showMenu);
      tether && tether.destroy();
    };
  }, [showMenu, hideMenu]);

  return { toggleRef, menuRef };
}
